import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { RefreshCw, MessageSquare, AlertCircle, CheckCircle, Clock, Database, Send } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface LogFilters {
  status: string;
  notificationType: string;
  search: string;
}

export function SystemLogsMonitor() {
  const [filters, setFilters] = useState<LogFilters>({
    status: 'all',
    notificationType: 'all',
    search: '',
  });

  // Fetch queue statistics
  const { data: queueStats, isLoading: statsLoading, refetch } = useQuery({
    queryKey: ['telegram-queue-stats'],
    queryFn: async () => {
      const { data: pending, error: pendingError } = await supabase
        .from('telegram_message_queue')
        .select('*', { count: 'exact' })
        .eq('status', 'pending');

      const { data: sent, error: sentError } = await supabase
        .from('telegram_message_queue')
        .select('*', { count: 'exact' })
        .eq('status', 'sent')
        .gte('updated_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      const { data: failed, error: failedError } = await supabase
        .from('telegram_message_queue')
        .select('*', { count: 'exact' })
        .eq('status', 'failed');

      if (pendingError || sentError || failedError) {
        throw pendingError || sentError || failedError;
      }

      return {
        pending: pending?.length || 0,
        sent: sent?.length || 0,
        failed: failed?.length || 0,
      };
    },
    refetchInterval: 10000,
  });

  // Fetch filtered messages
  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['system-logs', filters],
    queryFn: async () => {
      let query = supabase
        .from('telegram_message_queue')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      // Apply filters
      if (filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters.notificationType !== 'all') {
        query = query.contains('metadata', { notification_type: filters.notificationType });
      }

      if (filters.search) {
        query = query.ilike('message_text', `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
    refetchInterval: 10000,
  });

  const handleProcessQueue = async () => {
    try {
      const { error } = await supabase.functions.invoke('telegram-queue-processor');
      
      if (error) throw error;
      
      toast.success('Queue processing triggered');
      refetch();
    } catch (error) {
      toast.error(`Failed to process queue: ${(error as Error).message}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'default';
      case 'failed':
        return 'destructive';
      case 'pending':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'normal':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ctrader_error: '⚠️ cTrader Error',
      signal_update: '📊 Signal Update',
      bot_health: '🏥 Bot Health',
      system: '⚙️ System',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-warning" />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{queueStats?.pending || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Messages waiting to be sent
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              Sent (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{queueStats?.sent || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Successfully sent in last 24 hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{queueStats?.failed || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Messages that failed to send
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Messages */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  System Logs
                </CardTitle>
                <CardDescription>Monitor queue messages and notifications</CardDescription>
              </div>
              <Button
                onClick={handleProcessQueue}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Process Queue
              </Button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.notificationType}
                onValueChange={(value) => setFilters({ ...filters, notificationType: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ctrader_error">cTrader Errors</SelectItem>
                  <SelectItem value="signal_update">Signal Updates</SelectItem>
                  <SelectItem value="bot_health">Bot Health</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder="Search messages..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {messagesLoading ? (
            <p className="text-center text-muted-foreground py-8">Loading...</p>
          ) : !messages || messages.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No messages found</p>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => {
                const metadata = message.metadata as Record<string, any> || {};
                const notificationType = metadata.notification_type;
                
                return (
                  <div
                    key={message.id}
                    className="flex items-start gap-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    {getStatusIcon(message.status)}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {/* Source Badge */}
                        <Badge variant="outline" className="gap-1">
                          <Send className="h-3 w-3" />
                          Queue
                        </Badge>

                        {/* Notification Type Badge */}
                        {notificationType && (
                          <Badge variant="secondary">
                            {getNotificationTypeLabel(notificationType)}
                          </Badge>
                        )}

                        {/* Status Badge */}
                        <Badge variant={getStatusColor(message.status)}>
                          {message.status}
                        </Badge>

                        {/* Priority Badge */}
                        <Badge variant={getPriorityColor(String(message.priority))}>
                          {message.priority}
                        </Badge>

                        <span className="text-xs text-muted-foreground">
                          Chat: {message.chat_id}
                        </span>
                      </div>
                      
                      <p className="text-sm mb-2">{message.message_text}</p>
                      
                      {/* Metadata Display */}
                      {(metadata.entity_type || metadata.entity_id) && (
                        <div className="text-xs text-muted-foreground mb-2">
                          {metadata.entity_type && (
                            <span className="mr-3">Entity: {metadata.entity_type}</span>
                          )}
                          {metadata.entity_id && (
                            <span>ID: {metadata.entity_id}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>
                          Created {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                        </span>
                        {message.status === 'sent' && (
                          <span>
                            Updated {formatDistanceToNow(new Date(message.updated_at), { addSuffix: true })}
                          </span>
                        )}
                        {message.retry_count > 0 && (
                          <span className="text-warning">
                            Retries: {message.retry_count}/{message.max_retries}
                          </span>
                        )}
                      </div>
                      
                      {message.error_message && (
                        <p className="text-xs text-destructive mt-2 p-2 bg-destructive/10 rounded">
                          Error: {message.error_message}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
